import {
  Cake,
  Camera,
  Car,
  Church,
  Clock,
  Disc3,
  Eye,
  Flower2,
  Gem,
  Gift,
  Heart,
  type LucideIcon,
  MapPin,
  Martini,
  Mic,
  Music,
  PartyPopper,
  ScrollText,
  Shirt,
  Sparkles,
  Sunset,
  Utensils,
  Wine,
} from "lucide-react";

import i18n, { type TranslationKey } from "@/i18n";
import type { TimelineIconKey } from "@/lib/types";

export interface TimelineIconOption {
  key: TimelineIconKey;
  labelKey: TranslationKey;
  Icon: LucideIcon;
}

/**
 * The curated wedding-timeline icon set offered in the editor. Each key maps
 * to a lucide glyph here (admin) and a @tabler glyph in the uploader. Keep the
 * keys in sync with wedding-upload-be/functions/src/timeline.ts.
 */
export const TIMELINE_ICON_OPTIONS: TimelineIconOption[] = [
  { key: "getting-ready", labelKey: "timelineData.icons.getting-ready", Icon: Shirt },
  { key: "first-look", labelKey: "timelineData.icons.first-look", Icon: Eye },
  { key: "ceremony", labelKey: "timelineData.icons.ceremony", Icon: Church },
  { key: "rings", labelKey: "timelineData.icons.rings", Icon: Gem },
  { key: "vows", labelKey: "timelineData.icons.vows", Icon: ScrollText },
  { key: "photos", labelKey: "timelineData.icons.photos", Icon: Camera },
  { key: "cocktails", labelKey: "timelineData.icons.cocktails", Icon: Martini },
  { key: "toast", labelKey: "timelineData.icons.toast", Icon: Wine },
  { key: "dinner", labelKey: "timelineData.icons.dinner", Icon: Utensils },
  { key: "cake", labelKey: "timelineData.icons.cake", Icon: Cake },
  { key: "speeches", labelKey: "timelineData.icons.speeches", Icon: Mic },
  { key: "first-dance", labelKey: "timelineData.icons.first-dance", Icon: Disc3 },
  { key: "music", labelKey: "timelineData.icons.music", Icon: Music },
  { key: "party", labelKey: "timelineData.icons.party", Icon: PartyPopper },
  { key: "flowers", labelKey: "timelineData.icons.flowers", Icon: Flower2 },
  { key: "sunset", labelKey: "timelineData.icons.sunset", Icon: Sunset },
  { key: "sparklers", labelKey: "timelineData.icons.sparklers", Icon: Sparkles },
  { key: "transport", labelKey: "timelineData.icons.transport", Icon: Car },
  { key: "gift", labelKey: "timelineData.icons.gift", Icon: Gift },
  { key: "heart", labelKey: "timelineData.icons.heart", Icon: Heart },
  { key: "venue", labelKey: "timelineData.icons.venue", Icon: MapPin },
  { key: "clock", labelKey: "timelineData.icons.clock", Icon: Clock },
];

/** Default icon when an event has none chosen. */
export const DEFAULT_TIMELINE_ICON: TimelineIconKey = "clock";

const ICON_BY_KEY = new Map<TimelineIconKey, LucideIcon>(
  TIMELINE_ICON_OPTIONS.map((option) => [option.key, option.Icon]),
);

/** Resolves an icon key to its lucide component, falling back to a clock. */
export function getTimelineIcon(key: string): LucideIcon {
  return ICON_BY_KEY.get(key as TimelineIconKey) ?? Clock;
}

/** Human-readable label for an icon key (used in summaries / a11y). */
export function getTimelineIconLabel(key: string): string {
  const option = TIMELINE_ICON_OPTIONS.find((o) => o.key === key);
  return i18n.t(option?.labelKey ?? "timelineData.event");
}
